import React, { type ReactNode } from 'react';
import { Box, Paper, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  type DecoratedSectionIcon,
  getDecoratedSectionAccent,
  getDecoratedSectionIcon,
} from './decoratedSectionIcons';

export type { DecoratedSectionIcon, NotesSectionIcon } from './decoratedSectionIcons';

/** Matches subtitle2 cap height so the badge lines up with the title row. */
const SECTION_HEADER_ICON_SIZE = 24;

export interface NotesSectionHeaderProps {
  title: string;
  description?: string;
  count?: number;
  icon?: DecoratedSectionIcon;
  actions?: ReactNode;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export const NotesSectionHeader: React.FC<NotesSectionHeaderProps> = ({
  title,
  description,
  count,
  icon = 'notes',
  actions,
  onClick,
  sx,
}) => {
  const theme = useTheme();
  const Icon = getDecoratedSectionIcon(icon);
  const accentMain = getDecoratedSectionAccent(theme, icon);
  const countSuffix = count != null && count > 0 ? ` (${count})` : '';

  return (
    <Stack
      direction="row"
      alignItems={description ? 'flex-start' : 'center'}
      spacing={1.25}
      onClick={onClick}
      sx={[
        {
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        },
        onClick
          ? {
              cursor: 'pointer',
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              '&:hover': { bgcolor: 'action.hover' },
            }
          : undefined,
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: SECTION_HEADER_ICON_SIZE,
          height: SECTION_HEADER_ICON_SIZE,
          borderRadius: 1,
          bgcolor: alpha(accentMain, 0.12),
          color: accentMain,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16, display: 'block' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          component="div"
          sx={{
            lineHeight: `${SECTION_HEADER_ICON_SIZE}px`,
            m: 0,
          }}
        >
          {title}
          {countSuffix}
        </Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Box sx={{ flexShrink: 0, alignSelf: description ? 'flex-start' : 'center' }}>{actions}</Box>
      ) : null}
    </Stack>
  );
};

export interface NotesSectionProps {
  title: string;
  description?: string;
  count?: number;
  icon?: DecoratedSectionIcon;
  headerAction?: ReactNode;
  variant?: 'card' | 'plain';
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/** Decorated section block: header with icon and optional card body. */
const NotesSection: React.FC<NotesSectionProps> = ({
  title,
  description,
  count,
  icon = 'notes',
  headerAction,
  variant = 'card',
  children,
  sx,
}) => {
  const theme = useTheme();
  const accentMain = getDecoratedSectionAccent(theme, icon);

  const body =
    variant === 'card' ? (
      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          p: 1.5,
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          borderLeftWidth: 3,
          borderLeftStyle: 'solid',
          borderLeftColor: accentMain,
          bgcolor: alpha(accentMain, 0.04),
        }}
      >
        {children}
      </Paper>
    ) : (
      <Box sx={{ mt: 1 }}>{children}</Box>
    );

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', ...sx }}>
      <NotesSectionHeader
        title={title}
        description={description}
        count={count}
        icon={icon}
        actions={headerAction}
      />
      {body}
    </Box>
  );
};

export default NotesSection;
